import React, { type ReactElement, useState } from 'react';

import { usePathname } from 'next/navigation';

import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { type AllProductsQueryParams } from '@/common/types/product.type';
import { type FilteredDataType } from '@/features/store/types/filter.type';
import classNames from 'classnames';
import { useRouter } from 'nextjs-toploader/app';
import { isDesktop } from 'react-device-detect';

import Button from '@/common/components/button/button.component';
import CardDivider from '@/common/components/card-divider';
import Drawer from '@/common/components/drawer/drawer.component';
import FilterMultiLevelMenu from '@/common/components/filter-multi-level-menu';
import Switch from '@/common/components/switch/switch.component';
import Text from '@/common/components/text/text.component';
import { orderMock } from '@/common/constants/filter-product-list.constant';
import {
  type InputType,
  createMenuItems,
} from '@/common/utils/create-menu-items.utility.ts';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';

interface Props {
  payloadSetter?: (payload: AllProductsQueryParams) => void;
  categoryList: MultiLevelMenuItem[];
  filteredData: FilteredDataType;
  children?: ReactElement;
  wrapperClassName?: string;
}

function Filter({
  categoryList,
  payloadSetter,
  wrapperClassName,
  filteredData,
  children,
}: Props) {
  const router = useRouter();
  const pathName = usePathname();
  const [menuLevel, setMenuLevel] = useState<number>(0);
  const [open, setOpen] = useState(false);

  // Local state for drawer selections
  const [localFilters, setLocalFilters] =
    useState<FilteredDataType>(filteredData);

  // Sync local filters with prop when drawer opens
  const handleOpen = () => {
    setLocalFilters(filteredData);
    setOpen(true);
  };

  const handleClick = () => {
    setQueryParams();
    setOpen(false);
  };

  const setQueryParams = () => {
    const formatedCategory = localFilters.category.map((item) => item.value);
    const queryString = `${pathName}${createQueryStringUtility({
      ...(formatedCategory.length && {
        category: `${formatedCategory.join('-')}`,
      }),
      ...(localFilters.orderBy.value && {
        orderBy: `${localFilters.orderBy.value}`,
      }),
      ...(localFilters.isDiscounted && {
        isDiscounted: `${localFilters.isDiscounted}`,
      }),
      ...(localFilters.isAvailable && {
        isAvailable: `${localFilters.isAvailable}`,
      }),
    })}`;

    router.replace(queryString);
  };

  const onSelectMenuItem = (
    item: MultiLevelMenuItem | MultiLevelMenuItem[]
  ) => {
    setLocalFilters((prevState) => {
      if (Array.isArray(item)) {
        return {
          ...prevState,
          category: item,
        };
      } else {
        return {
          ...prevState,
          orderBy: item,
        };
      }
    });
  };

  const filterMenu = () => {
    const menu = !isDesktop
      ? [
          {
            label: 'مرتب سازی بر اساس',
            value: 'ORDERBY',
            subNestedItem: [...orderMock],
          },
        ]
      : [
          {
            label: 'مرتب سازی بر اساس',
            value: 'ORDERBY',
            inputType: 'title' as InputType,
          },
          ...orderMock,
        ];
    if (!isDesktop)
      menu.unshift({
        label: 'دسته‌بندی محصولات',
        value: 'CATEGORY',
        subNestedItem: [...categoryList],
      });
    return createMenuItems(menu);
  };

  return (
    <>
      <div className={classNames(wrapperClassName)} onClick={handleOpen}>
        {children}
      </div>

      <Drawer
        rootClassName="lg:!right-0 lg:!translate-x-0 lg:!w-full"
        className="category-drawer flex flex-col justify-between rounded-t-3xl lg:rounded-none"
        placement={!isDesktop ? 'bottom' : 'right'}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        height={'auto'}
      >
        <div className="max-lg:h-[calc(100%-80px)] flex flex-col justify-between overflow-y-auto lg:!h-full">
          <div>
            <FilterMultiLevelMenu
              menuLevel={menuLevel}
              isDesktop={isDesktop}
              setMenuLevel={setMenuLevel}
              onSelect={onSelectMenuItem}
              title="فیلتر و مرتب سازی"
              items={filterMenu()}
              defaultChecked={{
                category: localFilters.category,
                orderBy: localFilters.orderBy,
              }}
            />
            {menuLevel === 0 && (
              <>
                <div className="flex items-center justify-between py-[12px] lg:!pb-[16] lg:!pt-0">
                  <Text className="text-sm font-normal">فقط دارای تخفیف</Text>
                  <Switch
                    id="discount"
                    checked={localFilters.isDiscounted}
                    onChange={(e) => {
                      setLocalFilters({ ...localFilters, isDiscounted: e });
                    }}
                    label=""
                  />
                </div>
                <CardDivider
                  type="item"
                  itemClassName="hidden lg:!flex !px-0"
                />
                <div className="flex items-center justify-between pb-[12px] lg:!py-[16px]">
                  <Text className="text-sm font-normal">فقط محصولات موجود</Text>
                  <Switch
                    id="available"
                    checked={localFilters.isAvailable}
                    onChange={(e) => {
                      setLocalFilters({ ...localFilters, isAvailable: e });
                    }}
                    label=""
                  />
                </div>
              </>
            )}
          </div>
          {menuLevel === 0 && (
            <Button
              type="black"
              onClick={handleClick}
              id="store-submit-filter-v1"
              className="w-full"
            >
              اعمال
            </Button>
          )}
        </div>
      </Drawer>
    </>
  );
}

export default Filter;
